import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router";
import { Lock, Eye, EyeOff, CheckCircle2, AlertCircle, Users, ArrowLeft } from "lucide-react";
import axios from "axios";
import config from "../../../config";

export default function ActivateParentPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const email = params.get("email") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // In prototype mode a token is not needed — just show the form
  const isPrototype = config.appMode === "prototype";

  const strength = password.length >= 12 ? "Strong" : password.length >= 8 ? "Good" : password.length >= 4 ? "Weak" : "";
  const strengthColor = strength === "Strong" ? "bg-emerald-500" : strength === "Good" ? "bg-amber-400" : "bg-red-400";
  const strengthWidth = strength === "Strong" ? "w-full" : strength === "Good" ? "w-2/3" : strength === "Weak" ? "w-1/3" : "w-0";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords don't match."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setError("");
    setLoading(true);
    try {
      if (isPrototype) {
        await new Promise((r) => setTimeout(r, 800));
        setSuccess(true);
        return;
      }
      // Use the reset-password endpoint with the invite token
      await axios.post(`${config.apiBaseUrl}/api/v1/auth/reset-password/${token}`, { password });
      setSuccess(true);
      setTimeout(() => navigate("/parent/signin"), 2500);
    } catch (err) {
      setError(err?.response?.data?.error?.message || "Activation failed. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-violet-50/30 to-indigo-50/20 p-4">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-1/4 w-72 h-72 bg-violet-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-indigo-200/20 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10 space-y-6">
        <Link to="/parent/signin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 font-medium transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Parent sign in
        </Link>

        <div className="backdrop-blur-xl bg-white/60 border border-white/70 rounded-3xl p-8 sm:p-10 shadow-2xl">
          {success ? (
            <div className="text-center space-y-4">
              <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">Account activated!</h1>
              <p className="text-slate-500 text-sm">
                Your parent account is ready. You can now sign in to view your child's progress.
              </p>
              <button onClick={() => navigate("/parent/signin")}
                className="w-full py-3 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold text-sm transition-colors">
                Go to parent sign in
              </button>
            </div>
          ) : (
            <>
              <div className="mb-7 space-y-1">
                <div className="w-12 h-12 bg-violet-100 rounded-2xl flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-violet-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900">Activate your account</h1>
                <p className="text-slate-500 text-sm">
                  {email
                    ? <>Welcome! Set a password for <span className="font-semibold text-slate-700">{email}</span>.</>
                    : "Set a password to activate your parent account."}
                </p>
              </div>

              {error && (
                <div className="mb-5 p-4 bg-red-50/80 border border-red-200/50 rounded-xl flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
                  <span className="text-red-800 font-medium text-sm">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">New password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type={show ? "text" : "password"} value={password}
                      onChange={(e) => { setPassword(e.target.value); setError(""); }}
                      placeholder="Min. 8 characters" required
                      className="w-full pl-11 pr-11 py-3.5 bg-white/70 border border-slate-200/80 rounded-xl focus:bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 text-sm" />
                    <button type="button" onClick={() => setShow(!show)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                      {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {password && (
                    <div className="space-y-1">
                      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-300 ${strengthColor} ${strengthWidth}`} />
                      </div>
                      <p className="text-xs text-slate-400">{strength} password</p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Confirm password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type={show ? "text" : "password"} value={confirm}
                      onChange={(e) => { setConfirm(e.target.value); setError(""); }}
                      placeholder="Repeat your password" required
                      className={`w-full pl-11 pr-4 py-3.5 bg-white/70 border rounded-xl focus:bg-white focus:ring-2 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 text-sm ${
                        confirm && confirm !== password
                          ? "border-red-300 focus:border-red-400 focus:ring-red-500/20"
                          : "border-slate-200/80 focus:border-violet-500 focus:ring-violet-500/20"
                      }`} />
                  </div>
                  {confirm && confirm !== password && (
                    <p className="text-xs text-red-500">Passwords don't match</p>
                  )}
                </div>

                <button type="submit" disabled={loading || !password || !confirm}
                  className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-semibold text-sm shadow-lg transition-all disabled:opacity-50">
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Activating…
                    </span>
                  ) : "Activate account"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
